/**
 * The console's select: a closed list of values, opened in the same panel as
 * the Combobox rather than the browser's own option list. finixt-ui's Select
 * is a native <select> under a border, so Chrome drew its list; this one
 * keeps every list in the console looking and behaving alike.
 *
 * The panel gets a search row when the list passes SEARCH_THRESHOLD options,
 * or always when `searchable` marks a list of records, which grow.
 */
import type { ReactElement } from 'react';

import { Combobox, type ComboboxOption } from './Combobox.js';

/** A list longer than this gets the search row without being asked. */
const SEARCH_THRESHOLD = 8;

/** A value, or a value with the label shown for it, and the panel's other parts. */
export type SelectOption = string | ComboboxOption;

export interface SelectProps {
  options: readonly SelectOption[];
  value: string;
  /** Receives the chosen option's value. */
  onChange: (value: string) => void;
  /** The trigger's id, so a Field label names it. */
  id?: string | undefined;
  'aria-label'?: string | undefined;
  /** Shown when no option holds the value. */
  placeholder?: string | undefined;
  disabled?: boolean | undefined;
  /** The list holds records: always draw the search row. */
  searchable?: boolean | undefined;
  searchPlaceholder?: string | undefined;
  size?: 'sm' | 'md' | undefined;
  /** The trigger's width; the default fills the container. */
  width?: number | string | undefined;
  /** The panel's least width, when it should open wider than the field. */
  panelWidth?: number | undefined;
}

/** A closed list of values in the console's panel. */
export function Select({
  options,
  searchable,
  placeholder = 'Choose…',
  searchPlaceholder = 'Search…',
  ...rest
}: SelectProps): ReactElement {
  const normalized = options.map((option) =>
    typeof option === 'string' ? { value: option, label: option } : option,
  );
  return (
    <Combobox
      {...rest}
      options={normalized}
      placeholder={placeholder}
      searchPlaceholder={searchPlaceholder}
      searchable={searchable ?? normalized.length > SEARCH_THRESHOLD}
    />
  );
}
